import type { TemplateRef } from '@angular/core'

import type {
  ArticlesDto,
  AuthorDto,
  EditionDto,
  MagazineDto,
} from '../api/responses'
import type { DateRange } from '../services/date-service/date-range.model'

/**
 * Interface extending AuthorDto with additional context information Used for
 * preserving editor/author context in navigation
 */
export interface AuthorWithContext extends AuthorDto {
  linkContext?: 'author' | 'editor'
  linkType?: 'author' | 'editor'
}

export interface AccordionTemplateContext {
  section: AccordionSection
  data: MoreInfoData | ForWhomData | EditorialData | PricesData
}

export interface AccordionSection {
  title: string
  componentName:
    | 'more-info'
    | 'for-whom'
    | 'editorial'
    | 'prices'
    | 'editorial-secretariat'
    | 'author-guidelines'
    | 'editions'
    | 'articles'
  expanded: boolean
  template?: TemplateRef<AccordionTemplateContext>
  content?: string
}

export interface MoreInfoData {
  publications: Publication[]
}

export interface ForWhomData {
  targetAudience: string[]
}

export interface EditorialData {
  editors: Editor[]
}

export interface PricesData {
  pricingDetails: Pricing[]
}

export interface Person {
  name: string
  company_name: string
}

export interface Editor {
  name: string
  position: string
  company_name: string
  linkedIn?: string
}

export interface Section {
  title: string
  editors?: Editor[]
  content?: string
  imageUrl?: string
}

export interface IndexItem {
  level: number
  title: string
  link: string
}

export interface BreadcrumbItem {
  label: string | number
  url: string
  params?: Record<string, string | number>
}

export interface YearChip {
  label: string
  value: number | null // Allow null for "Alle"
  count: number
}

export interface Publication {
  title: string
  description: string
  journal: string
  date: string
}

export interface Pricing {
  type: string
  price: number
}

export interface ArticleDetails {
  title: string
  journal: string
  content: string
}

export interface AuthorSummary {
  name: string
  company_name: string
  department: string
}

// Alternative approach if you want to keep the original Section interface unchanged
export interface EditorialSection {
  title: string
  editors?: Editor[]
  content?: string
  imageUrl?: string
}

export interface EditionGroup {
  year: number
  editions: EditionDto[]
}

export class ArticleOptions {
  fetchDetails = true
  limit = -1
}

export type EditorialBoardRole =
  'chiefEditor' | 'editor' | 'permanentEmployee' | 'editorialSecretary'

export type FetchPolicy = 'cache-first' | 'network-only'

export interface ArticleWithNullableRefs extends Omit<
  ArticlesDto,
  'magazine' | 'edition'
> {
  magazine: (MagazineDto & { title: string | null }) | null
  edition: EditionDto | null
  updatedAt?: string
}

export interface SearchArticlesResponse {
  articles: ArticlesDto[]
  totalDocs: number
}

export type PaginationItem =
  { type: 'page'; value: number } | { type: 'ellipsis' }

export interface PaginationConfig {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  itemsPerPage?: number
  showItemsPerPage?: boolean
  onItemsPerPageChange?: (itemsPerPage: number) => void
}

export interface AuthorGroup {
  letter: string
  authors: AuthorDto[]
}

export type SortOrder = 'asc' | 'desc'

export interface EditionFilter {
  magazine?: { equals: number }
  year?: { equals: number }
}

export interface TranslationObject {
  [key: string]: string | TranslationObject
}

export interface ArticleSearchParams {
  searchTerm?: string
  magazineIds?: number[]
  authorIds?: number[]
  articleTypes?: string[]
  dateRange?: DateRange
  page?: number
  limit?: number
}

export interface SearchTermsResult {
  firstName: string
  lastName: string
  dateRange?: DateRange
  searchTerms: string
}

export interface TermContext {
  term: string
  index: number
  originalPhrase: string
}

export type TranslationParams = Record<string, string | number>

export interface GetArticlesByMagazineIdVariables {
  magazineId: string
  page: number
  limit: number
  searchTerm?: string
}

export enum ArticleType {
  ACTUALIA_ARTICLE = 'actualia-article',
  ACTUALIA_OVERVIEW = 'actualia-overview',
  ARTICLE = 'article',
  CASE_LAW_ACTUALIA_OVERVIEW = 'case-law-actualia-overview',
  CASE_LAW_ARTICLE = 'case-law-article',
  CASE_LAW_DISCUSSION = 'case-law-discussion',
  CASE_LAW_OVERVIEW = 'case-law-overview',
  PREFACE = 'preface',
  ANNOTATION = 'annotation',
  RUBRIC = 'rubric',
  CURRENT_AFFAIRS = 'current-affairs',
  OTHER = 'other',
  LAW_AND_REGULATION = 'law-and-regulation',
  RENT = 'rent',
  PROJECT_DEVELOPMENT = 'project-development',
  PURCHASE = 'purchase',
  CONSTRUCTION = 'construction',
}

/**
 * The subset of `ArticleType` values that should be selectable in the article
 * type filter UI. `ArticleType` also contains category-style values (e.g.
 * `RENT`, `CONSTRUCTION`) that are not actual article types and must not appear
 * as filter options.
 */
export const FILTERABLE_ARTICLE_TYPES: ArticleType[] = [
  ArticleType.ACTUALIA_ARTICLE,
  ArticleType.ACTUALIA_OVERVIEW,
  ArticleType.ARTICLE,
  ArticleType.CASE_LAW_ACTUALIA_OVERVIEW,
  ArticleType.CASE_LAW_ARTICLE,
  ArticleType.CASE_LAW_DISCUSSION,
  ArticleType.CASE_LAW_OVERVIEW,
  ArticleType.PREFACE,
  ArticleType.ANNOTATION,
  ArticleType.RUBRIC,
]

/**
 * The API only supports filtering `article_type` via `equals`/`not_equals`
 * against the _entire_ `{ name, title }` object (no `in`, no filtering by
 * `name` alone). These are the exact `title` strings the backend currently
 * returns per type — required to build a matching `equals` condition. If the
 * backend ever changes a type's title text, filtering on that type will
 * silently stop matching until this map is updated.
 */
export const ARTICLE_TYPE_BACKEND_TITLES: Partial<Record<ArticleType, string>> =
  {
    [ArticleType.ACTUALIA_ARTICLE]: 'Nieuwsbericht',
    [ArticleType.ACTUALIA_OVERVIEW]: 'Nieuwsoverzicht',
    [ArticleType.ARTICLE]: 'Artikelen',
    [ArticleType.CASE_LAW_ACTUALIA_OVERVIEW]: 'Uitspraken en nieuws',
    [ArticleType.CASE_LAW_ARTICLE]: 'Uitspraak',
    [ArticleType.CASE_LAW_DISCUSSION]: 'Jurisprudentiebespreking',
    [ArticleType.CASE_LAW_OVERVIEW]: 'Jurisprudentieoverzicht',
    [ArticleType.PREFACE]: 'Voorwoord',
    [ArticleType.ANNOTATION]: 'Annotatie',
    [ArticleType.RUBRIC]: 'Rubriek (nieuws, wetgeving of jurisprudentie)',
    [ArticleType.CURRENT_AFFAIRS]: 'Actualia',
    [ArticleType.OTHER]: 'Overige',
    [ArticleType.LAW_AND_REGULATION]: 'Wet- en regelgeving',
    [ArticleType.RENT]: 'Huur',
    [ArticleType.PROJECT_DEVELOPMENT]: 'Projectontwikkeling',
    [ArticleType.PURCHASE]: 'Koop',
    [ArticleType.CONSTRUCTION]: 'Bouw',
  }

export type ArticleSearchSorting = { published_at: 'desc' | 'asc' }[]

export type ArticleSearchWhereConditions =
  | { published_at: { greater_than_equal: Date } }
  | { published_at: { less_than_equal: Date } }
  | { published_at: { not_equals: null } }
  | { authors: { in: number[] } }
  | { creator: { in: number[] } }
  | { magazine: { in: number[] } }
  | {
      OR: (
        | { users__authors: { in: number[] } }
        | { users__creators: { in: number[] } }
      )[]
    }
  | {
      OR: { article_type: { equals: { name: string; title: string } } }[]
    }
  | {
      OR: (
        | { title?: { contains: string } }
        | { preface?: { contains: string } }
        | { keywords__title?: { contains: string } }
        | { categories__title?: { contains: string } }
      )[]
    }

export interface ArticleSearchWhereClause {
  OR?: {
    title?: { contains: string }
    preface?: { contains: string }
  }[]
  AND?: ArticleSearchWhereConditions[]
}

export interface MenuItem {
  route: string
  icon: string
  label: string
  alt: string
  condition?: () => boolean
}

// Magazine item in product response
export interface MagazineItem {
  id: number
  title?: string
  media?: {
    cover?: {
      url?: string
    }
  }
}

export enum UserRole {
  Customer = 'customer',
  Editor = 'editor',
  AccountAdmin = 'account_admin',
  BillingAdmin = 'billing_admin',
  Admin = 'admin',
  InBridgeAccess = 'in_bridge_access',
  SuperAdmin = 'super_admin',
}

export interface ArticlePurchasePrice {
  // values excl. VAT
  amount: number
  /**
   * The configured student rate. Exposed to everyone, including visitors
   * without an account, so the discount can be advertised up front. Its
   * presence means the rate exists -- not that this visitor gets it.
   */
  studentAmount: number | null
  /** Whether this visitor is charged `studentAmount` by default. */
  studentPricingApplies: boolean
  taxPercentage: number
}

export interface PriceResult {
  // value excl btw
  value: number
  tax_rate: number
  error: string | null
}

export enum ContentAccessEnum {
  ARTICLE_PURCHASED = 'ARTICLE_PURCHASED',
  SEAT_ASSIGNED = 'SEAT_ASSIGNED',
  SEAT_AVAILABLE = 'SEAT_AVAILABLE',
  SEAT_UNAVAILABLE = 'SEAT_UNAVAILABLE',
  SUBSCRIPTION_NOT_STARTED = 'SUBSCRIPTION_NOT_STARTED',
  INVOICE_NOT_PAID = 'INVOICE_NOT_PAID',
}

// The access an article's full text is shown for.
export function hasFullTextAccess(access: ContentAccessEnum | null): boolean {
  return (
    access === ContentAccessEnum.SEAT_ASSIGNED ||
    access === ContentAccessEnum.ARTICLE_PURCHASED
  )
}
